import {useState, useMemo, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {buildPriceTestParam} from '@/helpers/sfccHelpers'

// Hooks
import {useToast} from '@/hooks/useToast'
import {useCurrentBasket} from '@/hooks/basket/useCurrentBasket'
import {useApproachingDiscounts} from '@/hooks/basket/useApproachingDiscounts'

// Helpers
import {getAdjustedOrderTotal} from '@/helpers/getAdjustedOrderTotal'

// Constants
import {
    API_ERROR_MESSAGE,
    TOAST_MESSAGE_REMOVED_ITEM_FROM_CART
} from '@salesforce/retail-react-app/app/constants'

// Utilities
import debounce from 'lodash/debounce'
import {
    useShopperBasketsMutation,
    useShippingMethodsForShipment,
    useProducts
} from '@salesforce/commerce-sdk-react'

const mergeExistingAndRemovedProducts = (basketProducts, removedProducts) => {
    // Calculate the total size
    const totalLength = basketProducts.length + removedProducts.length

    // Initialize an empty array of size `totalLength`
    const mergedProducts = new Array(totalLength)

    // Add removed products based on their index
    removedProducts.forEach(({data, productIndex}) => {
        mergedProducts[productIndex] = {
            isRemoved: true,
            data: data
        }
    })

    // Track the current position in the merged array for basketProducts
    let basketIndex = 0
    for (let i = 0; i < totalLength; i++) {
        // If the current index in mergedProducts is empty, add the next basket product
        if (!mergedProducts[i]) {
            mergedProducts[i] = {
                isRemoved: false,
                data: basketProducts[basketIndex]
            }
            basketIndex++
        }
    }

    return mergedProducts
}

const DEBOUNCE_WAIT = 750
const useMinicart = () => {
    const [removedProducts, setRemovedProducts] = useState([])
    const [conditionThreshold, setConditionThreshold] = useState(50)
    let {
        derivedData: {totalItems},
        data: basket,
        isLoading,
        refetchBaskets
    } = useCurrentBasket()
    const basketId = basket?.basketId

    const productSignature = basket?.productItems
        ? basket.productItems.map((item) => `${item.productId}:${item.quantity}`).join(',')
        : ''
    const {data: approachingDiscounts} = useApproachingDiscounts(basketId, {productSignature})

    // if we get a different threshold from OCAPI, update our state to reflect that
    useEffect(() => {
        if (
            Array.isArray(approachingDiscounts) &&
            approachingDiscounts[0]?.condition_threshold !== undefined
        ) {
            setConditionThreshold(approachingDiscounts[0].condition_threshold)
        }
    }, [approachingDiscounts])

    const productItems = useMemo(() => {
        if (isLoading || !basket?.productItems) return []
        return mergeExistingAndRemovedProducts(basket.productItems, removedProducts)
    }, [isLoading, basket?.productItems, removedProducts])

    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true,
                ...buildPriceTestParam()
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    /***************** Product Bundles ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    const {data: bundleChildProductData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds?.join(','),
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory))',
                ...buildPriceTestParam()
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    // We use the `products` object to reference products by itemId instead of productId
    // Since with product bundles, even though the parent productId is the same,
    // variant selection of the bundle children can be different,
    // and require unique references to each product bundle
    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            // calculate inventory for product bundles based on availability of children
            if (productItem?.bundledProductItems && bundleChildProductData) {
                let lowestStockLevel =
                    currentProduct?.inventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
                let productWithLowestInventory = ''
                productItem?.bundledProductItems.forEach((bundleChild) => {
                    const bundleChildStockLevel =
                        bundleChildProductData?.[bundleChild.productId]?.inventory?.stockLevel ??
                        Number.MAX_SAFE_INTEGER
                    lowestStockLevel = Math.min(lowestStockLevel, bundleChildStockLevel)
                    if (lowestStockLevel === bundleChildStockLevel)
                        productWithLowestInventory = bundleChild.productName
                })

                if (currentProduct?.inventory) {
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory
                        }
                    }
                }
            }
            updateProductsByItemId[productItem.itemId] = currentProduct
        })
        return updateProductsByItemId
    }, [basket, products, bundleChildProductData])

    /*****************Basket Mutation************************/
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    /*****************Basket Mutation************************/

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)

    const {formatMessage} = useIntl()
    const toast = useToast()

    /******************* Shipping Methods for basket shipment *******************/
    // do this action only if the basket shipping method is not defined
    // we need to fetch the shippment methods to get the default value before we can add it to the basket
    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me'
            }
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled:
                !!basket?.basketId &&
                basket.shipments.length > 0 &&
                !basket.shipments[0].shippingMethod,
            onSuccess: (data) => {
                updateShippingMethodForShipmentsMutation.mutate({
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: 'me'
                    },
                    body: {
                        id: data.defaultShippingMethodId
                    }
                })
            }
        }
    )

    /************************* Error handling ***********************/
    const showError = () => {
        toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }
    /************************* Error handling ***********************/

    /***************************** Update quantity **************************/
    const changeItemQuantity = debounce(async (quantity, product) => {
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        const previousQuantity = localQuantity[product.itemId]
        setLocalQuantity({...localQuantity, [product.itemId]: quantity})
        setCartItemLoading(true)
        setSelectedItem(product)

        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.id,
                    quantity: parseInt(quantity)
                }
            },
            {
                onSettled: () => {
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    void (async () => {
                        await refetchBaskets()
                        setLocalQuantity({...localQuantity, [product.itemId]: quantity})
                    })()
                },
                onError: () => {
                    setLocalQuantity({...localQuantity, [product.itemId]: previousQuantity})
                    showError()
                }
            }
        )
    }, DEBOUNCE_WAIT)

    const handleChangeItemQuantity = async (product, value) => {
        const stockLevel = productsByItemId?.[product.itemId]?.inventory?.stockLevel ?? 1

        // Handle removing of the items when 0 is selected.
        if (value === 0) {
            // Flush last call to keep ui in sync with data.
            changeItemQuantity.flush()

            // Set the selected item to the current product to the modal acts on it.
            setSelectedItem(product)

            // Return false as 0 isn't valid section.
            return false
        }

        // Cancel any pending handlers.
        changeItemQuantity.cancel()

        // Allow use to selected values above the inventory.
        if (value > stockLevel || value === product.quantity) {
            return true
        }

        // Take action.
        changeItemQuantity(value, product)

        return true
    }
    /***************************** Update quantity **************************/

    /***************************** Remove Item from basket **************************/
    const handleRemoveItem = async (product, productIndex) => {
        setSelectedItem(product)
        setCartItemLoading(true)
        await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket.basketId, itemId: product.itemId}
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    void (async () => {
                        await refetchBaskets()
                        setRemovedProducts([
                            ...removedProducts,
                            {
                                productIndex: productIndex,
                                data: product
                            }
                        ])
                        toast({
                            title: formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {
                                quantity: 1
                            }),
                            status: 'success'
                        })
                    })()
                },
                onError: () => {
                    showError()
                }
            }
        )
    }

    const hasEarnedFreeShipping = (basket?.productTotal ?? 0) >= conditionThreshold
    const adjustedOrderTotal = useMemo(
        () => getAdjustedOrderTotal(basket, hasEarnedFreeShipping),
        [basket, hasEarnedFreeShipping]
    )

    return {
        handleRemoveItem,
        productsByItemId,
        isProductsLoading,
        localQuantity,
        handleChangeItemQuantity,
        isCartItemLoading,
        selectedItem,
        totalItems,
        isLoading,
        productItems,
        setRemovedProducts,
        basket,
        approachingDiscounts,
        conditionThreshold,
        adjustedOrderTotal,
        hasEarnedFreeShipping
    }
}

export default useMinicart
